import { bigDecimal } from "js-big-decimal";
import log from "logbeacon";

/**
 * Safe decimal arithmetic utilities for financial calculations
 * Uses BigDecimal to avoid floating-point precision issues
 */

/**
 * Parse string to number safely, returns 0 for invalid input
 */
export function safeParseNumber(
  value: string | number | undefined | null,
): number {
  if (value === null || value === undefined || value === "") {
    return 0;
  }
  const parsed = typeof value === "string" ? parseFloat(value) : value;
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Format BigDecimal string for display with fixed decimal places
 * @param value - BigDecimal string to format
 * @param decimals - Maximum decimal places to show (default: 8)
 * @returns Formatted string with trailing zeros removed
 */
export function formatBigDecimalString(
  value: string | number | undefined | null,
  decimals: number = 8,
): string {
  if (!value || value === "0") {
    return "0";
  }

  const str = typeof value === "number" ? value.toString() : value;

  // Ensure the string has a decimal point
  if (!str.includes(".")) {
    return str;
  }

  const parts = str.split(".");
  const integerPart = parts[0] || "0";
  let decimalPart = parts[1] || "";

  // Truncate to max decimal places
  if (decimalPart.length > decimals) {
    decimalPart = decimalPart.substring(0, decimals);
  }

  // Remove trailing zeros from decimal part only
  decimalPart = decimalPart.replace(/0+$/, "");

  // Return integer only if no decimal part
  if (decimalPart === "") {
    return integerPart;
  }

  return integerPart + "." + decimalPart;
}

/**
 * Calculate total based on transaction type using BigDecimal
 * BUY: price * quantity + fees (total cost)
 * SELL: price * quantity - fees (net income)
 * @param quantity - Quantity value
 * @param price - Price per unit value
 * @param fees - Fees value
 * @param type - Transaction type ("BUY" or "SELL")
 * @param precision - Decimal places to keep (default: 8)
 * @returns Calculated total as number
 */
export function calculateTotal(
  quantity: string | number | undefined | null,
  price: string | number | undefined | null,
  fees: string | number | undefined | null,
  type: "BUY" | "SELL" = "BUY",
): string {
  try {
    const product = bigDecimal.multiply(price, quantity);
    const total =
      type === "BUY"
        ? bigDecimal.add(product, fees)
        : bigDecimal.subtract(product, fees);

    return total;
  } catch (error) {
    log.error("[DecimalMath] Error in calculateTotal:", error);
    return "";
  }
}
